import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { PodcastTaxonomyProgramRow } from "../../services/api";
import {
  buildProgramTopicGraph,
  type ProgramTopicGraphLink,
  type ProgramTopicGraphNode,
  type TopicMetric,
} from "./taxonomyHierarchy";

const PROGRAM_COLUMN_X = 0.18;
const TOPIC_COLUMN_X = 0.82;
const PADDING_Y = 48;
const NODE_MIN_RADIUS = 10;
const NODE_MAX_RADIUS_PROGRAM = 28;
const NODE_MAX_RADIUS_TOPIC = 32;

type LayoutNode = ProgramTopicGraphNode & {
  x: number;
  y: number;
  radius: number;
};

type LayoutLink = ProgramTopicGraphLink & {
  sourceNode: LayoutNode;
  targetNode: LayoutNode;
};

function nodeRadius(value: number, maxValue: number, kind: "program" | "topic"): number {
  const min = NODE_MIN_RADIUS;
  const max = kind === "program" ? NODE_MAX_RADIUS_PROGRAM : NODE_MAX_RADIUS_TOPIC;
  if (maxValue <= 0) return min;
  const t = Math.sqrt(value / maxValue);
  return min + t * (max - min);
}

function layoutBipartiteGraph(
  nodes: ProgramTopicGraphNode[],
  links: ProgramTopicGraphLink[],
  width: number,
  height: number,
): { nodes: LayoutNode[]; links: LayoutLink[] } {
  const programNodes = nodes.filter((node) => node.kind === "program");
  const topicNodes = nodes.filter((node) => node.kind === "topic");
  const maxProgramValue = Math.max(1, ...programNodes.map((node) => node.value));
  const maxTopicValue = Math.max(1, ...topicNodes.map((node) => node.value));

  const layoutPrograms = (list: ProgramTopicGraphNode[]): LayoutNode[] => {
    const usableHeight = height - PADDING_Y * 2;
    const step = list.length <= 1 ? 0 : usableHeight / (list.length - 1);
    return list.map((node, index) => ({
      ...node,
      x: width * PROGRAM_COLUMN_X,
      y: list.length <= 1 ? height / 2 : PADDING_Y + step * index,
      radius: nodeRadius(node.value, maxProgramValue, "program"),
    }));
  };

  const layoutTopics = (list: ProgramTopicGraphNode[]): LayoutNode[] => {
    const usableHeight = height - PADDING_Y * 2;
    const step = list.length <= 1 ? 0 : usableHeight / (list.length - 1);
    return list.map((node, index) => ({
      ...node,
      x: width * TOPIC_COLUMN_X,
      y: list.length <= 1 ? height / 2 : PADDING_Y + step * index,
      radius: nodeRadius(node.value, maxTopicValue, "topic"),
    }));
  };

  const laidOutPrograms = layoutPrograms(programNodes);
  const laidOutTopics = layoutTopics(topicNodes);
  const nodeById = new Map<string, LayoutNode>(
    [...laidOutPrograms, ...laidOutTopics].map((node) => [node.id, node]),
  );

  const laidOutLinks: LayoutLink[] = [];
  for (const link of links) {
    const sourceNode = nodeById.get(link.source);
    const targetNode = nodeById.get(link.target);
    if (!sourceNode || !targetNode) continue;
    laidOutLinks.push({ ...link, sourceNode, targetNode });
  }

  return { nodes: [...laidOutPrograms, ...laidOutTopics], links: laidOutLinks };
}

function linkPath(source: LayoutNode, target: LayoutNode): string {
  const sx = source.x + source.radius;
  const sy = source.y;
  const tx = target.x - target.radius;
  const ty = target.y;
  const cx = (sx + tx) / 2;
  return `M ${sx} ${sy} C ${cx} ${sy}, ${cx} ${ty}, ${tx} ${ty}`;
}

type ProgramTopicNetworkGraphProps = {
  programs: PodcastTaxonomyProgramRow[];
  colorBySlug: Map<string, string>;
  metric: TopicMetric;
  height?: number;
};

const ProgramTopicNetworkGraph: React.FC<ProgramTopicNetworkGraphProps> = ({
  programs,
  colorBySlug,
  metric,
  height = 480,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hoverId, setHoverId] = useState<string | null>(null);

  const graph = useMemo(
    () => buildProgramTopicGraph(programs, { metric, colorBySlug, topTopics: 12 }),
    [programs, metric, colorBySlug],
  );

  const layout = useMemo(() => {
    if (width <= 0 || graph.nodes.length === 0) {
      return { nodes: [] as LayoutNode[], links: [] as LayoutLink[] };
    }
    return layoutBipartiteGraph(graph.nodes, graph.links, width, height);
  }, [graph, width, height]);

  const maxLinkWeight = useMemo(
    () => Math.max(1, ...layout.links.map((link) => link.weight)),
    [layout.links],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(el);
    setWidth(el.clientWidth || 0);
    return () => observer.disconnect();
  }, []);

  const connectedIds = useMemo(() => {
    if (!hoverId) return null;
    const ids = new Set<string>([hoverId]);
    for (const link of layout.links) {
      if (link.source === hoverId || link.target === hoverId) {
        ids.add(link.source);
        ids.add(link.target);
      }
    }
    return ids;
  }, [hoverId, layout.links]);

  const linkOpacity = (link: LayoutLink): number => {
    if (!connectedIds) return 0.35;
    return connectedIds.has(link.source) && connectedIds.has(link.target) ? 0.75 : 0.08;
  };

  const nodeOpacity = (nodeId: string): number => {
    if (!connectedIds) return 1;
    return connectedIds.has(nodeId) ? 1 : 0.25;
  };

  if (graph.nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No program–topic relationships to visualize.
      </Typography>
    );
  }

  return (
    <Box>
      <StackLegend />
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height,
          overflow: "hidden",
          borderRadius: 1,
          bgcolor: "action.hover",
          mt: 1,
        }}
      >
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label="Program topic network">
            <text
              x={width * PROGRAM_COLUMN_X}
              y={20}
              textAnchor="middle"
              fontSize={11}
              fill="#616161"
              fontWeight={600}
            >
              Programs
            </text>
            <text
              x={width * TOPIC_COLUMN_X}
              y={20}
              textAnchor="middle"
              fontSize={11}
              fill="#616161"
              fontWeight={600}
            >
              Topics
            </text>
            <g>
              {layout.links.map((link) => {
                const strokeWidth = 1 + (link.weight / maxLinkWeight) * 6;
                const color = link.targetNode.color ?? "#8884d8";
                return (
                  <path
                    key={`${link.source}-${link.target}`}
                    d={linkPath(link.sourceNode, link.targetNode)}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeOpacity={linkOpacity(link)}
                    pointerEvents="none"
                  />
                );
              })}
              {layout.nodes.map((node) => {
                const fill =
                  node.kind === "program"
                    ? "#546e7a"
                    : node.color ?? "#8884d8";
                const label =
                  node.display.length > 18
                    ? `${node.display.slice(0, 17)}…`
                    : node.display;
                const labelX =
                  node.kind === "program" ? node.x - node.radius - 6 : node.x + node.radius + 6;
                const textAnchor = node.kind === "program" ? "end" : "start";

                return (
                  <g
                    key={node.id}
                    opacity={nodeOpacity(node.id)}
                    onMouseEnter={() => setHoverId(node.id)}
                    onMouseLeave={() => setHoverId(null)}
                    style={{ cursor: "default" }}
                  >
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={node.radius}
                      fill={fill}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                    <text
                      x={labelX}
                      y={node.y + 4}
                      textAnchor={textAnchor}
                      fontSize={10}
                      fill="#424242"
                      pointerEvents="none"
                    >
                      {label}
                    </text>
                    <title>
                      {node.display} — {node.value.toLocaleString()}
                      {metric === "weightedMentions" ? " view-weighted" : " mentions"}
                    </title>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </Box>
    </Box>
  );
};

function StackLegend() {
  return (
    <Typography variant="caption" color="text.secondary">
      Link thickness reflects topic weight per program. Hover a node to highlight its connections.
    </Typography>
  );
}

export default ProgramTopicNetworkGraph;
